/**
 * AlertBanner — tinted card with a leading icon + message + optional
 * action button. Tones drive the bg/border/text colours.
 *
 * Used by PastDueBanner (warning), Plans scheduled-change banner
 * (warning), Profile validation summary (danger), and any general
 * info / success messages.
 *
 * Built on top of Mantine `Alert` with our token-driven colour overrides.
 */
import { Alert } from '@mantine/core';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconInfoCircle,
} from '@tabler/icons-react';
import { type ReactNode } from 'react';

import { GhostButton, SecondaryButton } from '../ui/Button';

export type AlertTone = 'info' | 'warning' | 'danger' | 'success';

type TonePack = {
  bg: string;
  fg: string;
  border: string;
  icon: ReactNode;
};

const TONE_MAP: Record<AlertTone, TonePack> = {
  info:    { bg: 'var(--info-bg)',    fg: 'var(--info-fg)',    border: 'rgba(30,107,214,0.18)', icon: <IconInfoCircle size={18} /> },
  warning: { bg: 'var(--pending-bg)', fg: 'var(--pending-fg)', border: 'rgba(224,161,27,0.22)', icon: <IconAlertTriangle size={18} /> },
  danger:  { bg: 'var(--failed-bg)',  fg: 'var(--failed-fg)',  border: 'rgba(208,74,54,0.20)',  icon: <IconAlertCircle size={18} /> },
  success: { bg: 'var(--success-bg)', fg: 'var(--success-fg)', border: 'rgba(31,163,99,0.20)',  icon: <IconCheck size={18} /> },
};

export type AlertBannerProps = {
  tone: AlertTone;
  title?: string;
  body: ReactNode;
  /** Inline action button to the right of the message. */
  action?: { label: string; onClick: () => void };
  /** Show a close button — caller controls dismissal state. */
  onDismiss?: () => void;
};

export function AlertBanner({
  tone,
  title,
  body,
  action,
  onDismiss,
}: AlertBannerProps) {
  const t = TONE_MAP[tone];
  return (
    <Alert
      icon={t.icon}
      withCloseButton={Boolean(onDismiss)}
      onClose={onDismiss}
      styles={{
        root: {
          background: t.bg,
          color: t.fg,
          border: `1px solid ${t.border}`,
          borderRadius: 'var(--r-md)',
        },
        title: { color: t.fg, fontWeight: 600 },
        message: { color: t.fg, fontSize: 13 },
        icon: { color: t.fg },
      }}
      title={title}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>{body}</div>
        {action ? (
          tone === 'warning' || tone === 'danger' ? (
            <SecondaryButton onClick={action.onClick}>{action.label}</SecondaryButton>
          ) : (
            <GhostButton onClick={action.onClick}>{action.label}</GhostButton>
          )
        ) : null}
      </div>
    </Alert>
  );
}
